// The "Administrator" action: unlock editing on an item flagged custitem_cannoteditcpq.
//
// Kevin, 2026-08-27: "every function should work like it was however if i go into the action
// tab and i click administrator and type in the password then this new logic happens."
//
// So NOTHING here changes ordinary behaviour. cpq2 still refuses a locked item and still
// switches such a launch to New Config, exactly as before. This is a door you have to walk
// through on purpose: Audit -> Actions -> Administrator -> password. Until then the app
// behaves as it always did, and if the password is never typed nothing is different.
//
// It lives in the Actions tab at his direction ("you should have put the function in the
// actions tab") rather than the header, where it would sit in front of every configuration.
//
// Kevin, 2026-09-02: "the password thing should have been simple not so complicated.
// basically if you have the password then editing mode is unlocked." And then: "when i type
// it in i should see success or fail not when i save."
//
// So the answer is given HERE, at the button, with no round trip. There was a POST
// /api/admin-unlock that did this server-side; it 404'd because the API had not deployed and
// took the whole feature down with it, for a check that granted nothing. Comparing in the
// browser cannot be down -- and it costs nothing that matters, because it was never the
// gate: api/create-assembly re-validates the code on the request that WRITES, at every one
// of its gates, and fails closed. A browser that has convinced itself it is unlocked still
// cannot edit a locked item. See src/util/adminCode.ts for the trade he chose.
//
// The code is held in memory only -- no localStorage, no sessionStorage -- so admin mode
// dies with the tab rather than waiting for the next person on a shared machine.
import { useState } from "react";
import { useAnswers } from "../state/AnswersContext";
import { isAdminCode } from "../util/adminCode";

export default function AdminOverrideField() {
  const { lockedItemId, adminOverride, setAdminOverride, editExistingItem } = useAnswers();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const typed = code.trim();
    if (!typed) return;
    // ANSWERED NOW, not at save time. A wrong password stops here rather than becoming a
    // save that reports success and changes nothing.
    if (!isAdminCode(typed)) {
      setError("That password is not correct.");
      return;
    }
    setError(null);
    setAdminOverride(typed);
    setCode("");
    setOpen(false);
    // Re-enter Edit mode on the item that was refused, so Save & Submit updates it in place
    // instead of making a new part. Nothing to do if no locked item was launched: admin mode
    // is on, and the next locked item opened in this tab is editable.
    if (lockedItemId) editExistingItem(lockedItemId);
  };

  return (
    <div className="cpq-audit-card">
      <header>Administrator</header>

      {adminOverride ? (
        <div className="cpq-admin-override cpq-admin-override-on" role="status">
          <div>
            <strong>Administrator mode is on.</strong>{" "}
            {lockedItemId
              ? "The locked item will be edited in place rather than copied to a new part."
              : "A locked item opened in this tab can be edited in place."}{" "}
            Every save made this way is recorded against your name.
          </div>
          <button
            type="button"
            className="cpq-btn cpq-btn-sm"
            onClick={() => {
              // Back to normal: drop the code so nothing further can be saved to a locked
              // item. Deliberately does NOT leave Edit mode -- the server re-checks the
              // code on every write, so without it the lock is enforced again anyway, and
              // silently switching the user to New Config mid-edit would be worse.
              setAdminOverride(null);
              setCode("");
              setError(null);
            }}
          >
            Turn off
          </button>
        </div>
      ) : !open ? (
        <>
          <p className="cpq-audit-note">
            Everything works as normal unless you turn this on. With it on, an item flagged{" "}
            <code>custitem_cannoteditcpq</code> can be edited in place instead of being
            copied to a new part.
            {lockedItemId
              ? " The item this session launched (id " + lockedItemId + ") is one of those."
              : ""}
          </p>
          <button type="button" className="cpq-btn cpq-btn-sm" onClick={() => setOpen(true)}>
            Administrator…
          </button>
        </>
      ) : (
        <div className="cpq-admin-override">
          <label htmlFor="cpq-admin-code">Password</label>
          <input
            id="cpq-admin-code"
            type="password"
            autoComplete="off"
            autoFocus
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
          <button
            type="button"
            className="cpq-btn cpq-btn-sm"
            disabled={!code.trim()}
            onClick={submit}
          >
            Unlock
          </button>
          <button
            type="button"
            className="cpq-btn cpq-btn-sm"
            onClick={() => {
              setOpen(false);
              setCode("");
              setError(null);
            }}
          >
            Cancel
          </button>
          {error && (
            <p className="cpq-admin-override-err" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
